import type { LoginRequest } from '@ghp/domain'

import {
  enterFromLocal,
  refreshSession,
  signIn,
  signOut,
  type AuthApi,
  type SignInResult,
  type TerrainSession,
} from './session'
import type { openSessionDatabase } from './session-store'

type SessionDatabase = ReturnType<typeof openSessionDatabase>

/**
 * La session vue par React — `subscribe`/`getSnapshot` pour `useSyncExternalStore`, et le
 * routeur qui attend `ready` avant de garder une route.
 *
 * Le store ne déclenche **aucun appel réseau de lui-même** : `ready` n'est que la lecture
 * locale (D22 pt.1 — l'entrée ne dépend jamais du serveur). La montée en ligne est un geste
 * du code appelant : `retryRefresh()` au démarrage puis à chaque retour du réseau
 * (`main.tsx`) — c'est ce qui la rend testable et hors du chemin de l'entrée.
 */
export interface AuthStore {
  /** Résolue après la lecture locale — jamais après un appel réseau. */
  ready: Promise<TerrainSession>
  getSnapshot(): TerrainSession
  subscribe(listener: () => void): () => void
  signIn(credentials: LoginRequest): Promise<SignInResult>
  signOut(): Promise<void>
  /**
   * Monte en ligne si la session est dégradée ; sans effet sinon.
   *
   * ⚠️ **Y compris quand le serveur a déjà refusé le jeton** (`reason: 'rejected'`) : un refus
   * n'est pas définitif — une révocation se lève, une horloge se recale, et c'est le même appel
   * qui le constatera. Fermer la reprise sur ce doute figerait la tablette dans un état dont
   * elle ne pourrait plus sortir toute seule, et le bouton « Réessayer » du panneau de connexion
   * n'aurait plus rien à appeler.
   *
   * ⚠️ Sa réponse est **abandonnée** si la session a changé pendant l'appel (déconnexion,
   * connexion d'un autre agent) : elle répond à une question que plus personne ne pose.
   */
  retryRefresh(): Promise<void>
  /**
   * La synchro a reçu un `401` avec ce jeton : il est mort avant son heure, ou expiré alors
   * que la session se croit encore en ligne. Redescendre en dégradé est ce qui rend le
   * refresh de la passe suivante possible — sans ce retour, `retryRefresh` ne tenterait
   * plus rien et la file rejouerait le même jeton mort à perpétuité.
   *
   * ⚠️ Comparé au jeton **courant** : un refus arrivé après un refresh (une réponse lente,
   * une passe croisée) ne doit pas redescendre la session que ce refresh vient de remonter.
   *
   * ⚠️ Le doute posé est `not_attempted`, et **pas** `rejected` : ce qui vient d'être refusé est
   * le jeton d'**accès**, qui vaut quinze minutes (M23) et expire en cours de tournée pour des
   * raisons parfaitement normales. Le jeton de **rafraîchissement**, lui, n'a rien dit — c'est
   * la passe suivante qui le saura. Écrire « session expirée » ici enverrait l'agent ressaisir
   * ses identifiants toutes les quinze minutes.
   */
  tokenRefused(accessToken: string): void
}

export function createAuthStore(db: SessionDatabase, api: AuthApi): AuthStore {
  let session: TerrainSession = { state: 'signed_out' }
  const listeners = new Set<() => void>()

  function set(next: TerrainSession): void {
    session = next
    for (const listener of listeners) listener()
  }

  const ready = enterFromLocal(db).then((entered) => {
    set(entered)
    return entered
  })

  return {
    ready,
    getSnapshot: () => session,
    subscribe(listener) {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
    async signIn(credentials) {
      const result = await signIn(db, api, credentials)
      if (result.outcome === 'signed_in') set(result.session)
      return result
    },
    async signOut() {
      set(await signOut(db, api))
    },
    async retryRefresh() {
      if (session.state !== 'degraded') return

      const asked = session
      const refreshed = await refreshSession(db, api)

      // ⚠️ **Une réponse qui arrive après un changement de session ne s'applique pas.** Rien ne
      // borne la durée de l'appel (la 4G d'un chantier), et l'agent a pu se déconnecter pendant
      // l'attente, ou un autre se connecter : poser la réponse ramènerait la session quittée à
      // l'écran, ou ferait travailler l'entrant sous l'identité et l'équipe du sortant. Toute
      // écriture de la session remplace l'objet, donc l'identité suffit à le voir.
      if (session !== asked) return

      set(refreshed)
    },
    tokenRefused(accessToken) {
      if (session.state !== 'online' || session.accessToken !== accessToken) return
      set({
        state: 'degraded',
        reason: 'not_attempted',
        user: session.user,
        team: session.team,
      })
    },
  }
}
